<script setup lang="ts">
import UserIcon from '@/assets/svgs/icons/default-user.svg'
import { computed } from 'vue'
import { useTimer } from '@/composables/useTimer'

const {
  src,
  alt,
  size = 50,
  showRing = false,
  isActive = true,
  showTimerBorder = false,
  timeRemaining = 0
} = defineProps<{
  src?: string
  alt?: string
  size?: number
  showRing?: boolean
  isActive?: boolean
  showTimerBorder?: boolean
  timeRemaining?: number
}>()

const emit = defineEmits<{
  timeUp: []
}>()

const computedSize = computed(() => {
  const isMobile = window.matchMedia('(max-width: 768px)').matches
  return isMobile ? size - 10 : size
})

// Use the improved timer composable
const { progress } = useTimer({
  timeRemaining,
  autoStart: showTimerBorder && timeRemaining > 0,
  onTimeUp: () => emit('timeUp')
})

// Alias for backward compatibility
const timerProgress = progress
</script>

<template>
  <div
    class="avatar-root flex items-center justify-center rounded-full relative"
    :class="[showRing ? 'active-ring' : '', !isActive ? 'avatar-muted' : '', showTimerBorder ? 'timer-border' : '']"
    :style="{ width: `${computedSize}px`, height: `${computedSize}px`, '--timer-progress': timerProgress }"
  >
    <img v-if="src" :src="src" :alt="alt" class="w-full h-full object-cover rounded-full" />
    <UserIcon v-else class="w-full h-full" />
  </div>
</template>

<style scoped>
@keyframes avatarRingSpin {
  to {
    transform: rotate(360deg);
  }
}

.active-ring::before {
  content: '';
  position: absolute;
  inset: -2.5px; /* ring thickness */
  border-radius: 9999px;
  background: conic-gradient(from 0deg, #ffe872, #ffe872);
  animation: avatarRingSpin 1.2s linear infinite;
  /* Hollow out the middle to form a ring */
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
}

.avatar-muted {
  filter: grayscale(85%) saturate(60%);
  opacity: 0.55;
}

.timer-border::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background-color: #1a1a1a;
  opacity: 0.4;
  z-index: 0;
}

.timer-border::after {
  content: '';
  position: absolute;
  inset: -2.5px;
  border-radius: 9999px;
  background: conic-gradient(from 0deg, transparent 0deg, transparent calc((100 - var(--timer-progress)) * 3.6deg), #ffc57f calc((100 - var(--timer-progress)) * 3.6deg), #ffc57f 360deg);
  z-index: 1;
  -webkit-mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
  mask: radial-gradient(farthest-side, transparent calc(100% - 5px), #000 calc(100% - 5px));
}
</style>
